'use client'

import { useRouteContext } from '@/app/layouts/TenantLayout'
import { NavMain } from '@/components/nav-main'
import { NavUser } from '@/components/nav-user'
import { NetworkStatus } from '@/components/network-status/NetworkStatus'
import { OrgProjectSwitcher } from '@/components/switcher'
import { Sidebar, SidebarContent, SidebarFooter, SidebarHeader } from '@/components/ui/sidebar'
import { useCurrentPlan } from '@/hooks/access-control'
import { useBillingStatusRefresh } from '@/hooks/useBillingStatusRefresh'
import { usePlanExpiration } from '@/hooks/usePlanExpiration'
import { isOrganizationLevelRoute } from '@/lib/routerUtils'
import type { PlatformUser } from '@/stores/auth'
import { isConsoleUser } from '@/stores/auth'
import { useUser } from '@/stores/authStore'
import { isConsoleDeployment, isDemoDeployment } from '@/utils/deployment'
import { useIsAdmin, useIsOwner } from '@/utils/permissions'
import { useLocation, useNavigate } from '@tanstack/react-router'
import { ChevronLeft } from 'lucide-react'
import type * as React from 'react'
import { useMemo } from 'react'
import {
	type NavItem,
	filterNavItems,
	navData,
	organizationNavData,
	organizationSettingsNavData,
	settingsNavData,
} from '../data/nav-data'
import { BillingWarningBanner } from './banner/BillingWarningBanner'
// import { UpdateBanner } from './banner/UpdateBanner'
import { Button } from './ui/button'

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
	const location = useLocation()
	const pathname = location.pathname
	const navigate = useNavigate()
	const user = useUser() as PlatformUser
	const { orgId, tenantId } = useRouteContext()
	const isOwner = useIsOwner()
	const isAdmin = useIsAdmin()
	const isConsole = isConsoleDeployment() || isDemoDeployment()
	const isConsoleUserType = isConsoleUser(user)

	// Fetch billing status to keep the subscription and warning info in sync
	useBillingStatusRefresh({ enabled: !!orgId, organizationId: orgId })

	// Check plan expiration status
	const expirationStatus = usePlanExpiration()
	const isAccessBlocked = expirationStatus.isAccessBlocked

	// Check if we're on an organization-level route using route matching utility
	const isOrgLevelRoute = isOrganizationLevelRoute(pathname || '')

	// Show organization navigation if on organization-level route and user is owner
	const shouldShowOrganizationNav = isOrgLevelRoute && isOwner && orgId

	const currentPlan = useCurrentPlan()

	const filteredNavData = useMemo(() => {
		const showTabs = user?.['tenant-settings']?.frontend_settings?.show_tabs
		return filterNavItems(navData, { currentPlan, showTabs })
	}, [user, currentPlan])
	let navigationItems: NavItem[] = filteredNavData

	if (shouldShowOrganizationNav) {
		if (isDemoDeployment()) {
			// For demo deployment: show all items including Billing
			navigationItems = organizationNavData
		} else if (isConsole && isConsoleUserType) {
			// For console organizations: only show Billing
			navigationItems = organizationNavData.filter(item => item.id === 'org-billing')
		} else {
			// For platform organizations: show all except Billing
			navigationItems = organizationNavData.filter(item => item.id !== 'org-billing')
		}
	}

	const isSettingsRoute = pathname?.includes('/settings')
	const isOrgSettingsRoute = isSettingsRoute && isOrgLevelRoute

	// Admin-only tabs are hidden from non-admins, mirroring projectSettingsAdminRouteGuard.
	const settingsNavItems = (isOrgSettingsRoute ? organizationSettingsNavData : settingsNavData).filter(
		item => !item.adminOnly || isAdmin,
	)

	// Styling for disabled sidebar content when access is blocked
	const disabledClass = isAccessBlocked ? 'opacity-40 pointer-events-none select-none' : ''

	return (
		<Sidebar {...props}>
			<SidebarHeader>
				<div className={`px-2 py-4 ${disabledClass}`}>
					<OrgProjectSwitcher showProjectSwitcher={!isConsoleDeployment()} />
				</div>
			</SidebarHeader>
			<SidebarContent>
				{isSettingsRoute && (
					<Button
						variant='ghost'
						data-testid='sidebar-back-to-platform'
						className={`w-fit my-2 ml-3 flex items-center gap-2 ${disabledClass}`}
						onClick={() => {
							if (isOrgSettingsRoute) {
								navigate({
									to: `/orgs/${orgId}/inventory/all`,
								})
							} else {
								navigate({
									to: `/orgs/${orgId}/projects/${tenantId}/device-management/all`,
									search: { page: 1, limit: 20, order: 'desc' },
								})
							}
						}}>
						<ChevronLeft className='size-(--icon-standalone)' />
						<span className='font-medium'>Back to Platform</span>
					</Button>
				)}

				<div className={`flex flex-col flex-1 ${disabledClass}`}>
					<NavMain
						items={isSettingsRoute ? settingsNavItems : navigationItems}
						hideAllChildren={Boolean(isSettingsRoute || shouldShowOrganizationNav)}
					/>
				</div>

				<div className='mt-auto px-3 pb-3 flex flex-col gap-3 shrink-0'>
					{/* <UpdateBanner /> */}
					<BillingWarningBanner />
				</div>
			</SidebarContent>
			<SidebarFooter className='p-0'>
				<NetworkStatus />
				<div className={`border-t ${disabledClass}`}>
					<NavUser />
				</div>
			</SidebarFooter>
		</Sidebar>
	)
}
